/**
 * A transcript search's field and hits (TMMAPP-0148 U2 items 12–14), for the search in a session
 * and across sessions.
 *
 * - **The field** (`SearchField`) says the query rule ("Enter 2 to 100 characters.") when what's
 *   typed falls outside it, and shows the server's refusal (a `400`, or any failure) in its place.
 * - **The hits** (`SearchHits`): each says who (by its line's role: a scope request is labelled
 *   "Scope request", §D), when (the person's zone, through `format.ts`) and its snippet, as plain
 *   text (never HTML). A polite status says the count, "Searching…" or "No messages match.". Grouped
 *   by session across sessions, each group under its label. "More" reads the next page where the
 *   search pages.
 */
import { useEffect, useRef, type ReactNode, type Ref } from 'react';
import {
  Box,
  Button,
  InputAdornment,
  List,
  ListItemButton,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { AssistantSearchHit } from '@/api/assistant';
import { errorCopy } from '@/api/errorCopy';
import { ErrorCodes, isApiError } from '@/api/errors';
import { SearchIcon } from '@/components/icons';
import { formatInstant } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useMe } from '@/session/meHooks';
import { resolveTokens } from '@/theme/theme';
import { SEARCH_MAX_CHARS, type TranscriptSearch } from './useTranscriptSearch';

interface SearchFieldProps {
  search: TranscriptSearch;
  /** The field's accessible name and placeholder. */
  label: string;
  /** A shorter placeholder where the field is narrow; the label otherwise. */
  placeholder?: string | undefined;
  /** The field's input, to move focus to it when the search opens. */
  inputRef?: Ref<HTMLInputElement> | undefined;
  /** Escape in the field (the search in a session closes). */
  onEscape?: (() => void) | undefined;
  /** Extra controls at the field's end (Close). */
  end?: ReactNode;
}

export function SearchField({
  search,
  label,
  placeholder,
  inputRef,
  onEscape,
  end,
}: SearchFieldProps) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const refused =
    search.error === null
      ? null
      : isApiError(search.error) &&
          (search.error.status === 400 || search.error.code === ErrorCodes.VALIDATION_FAILED)
        ? t('assistant.search.rule')
        : errorCopy(t, search.error, 'assistant.search.error');
  const helper = refused ?? (search.outsideRule ? t('assistant.search.rule') : undefined);
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
      <TextField
        type="search"
        size="small"
        fullWidth
        value={search.text}
        onChange={(event) => search.change(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && onEscape !== undefined) {
            event.preventDefault();
            onEscape();
          }
        }}
        placeholder={placeholder ?? label}
        inputRef={inputRef}
        error={refused !== null}
        helperText={helper}
        slotProps={{
          htmlInput: { 'aria-label': label, maxLength: SEARCH_MAX_CHARS * 2 },
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <Box component="span" aria-hidden sx={{ display: 'flex', color: tokens.text3 }}>
                  <SearchIcon />
                </Box>
              </InputAdornment>
            ),
          },
        }}
        data-search-field=""
      />
      {end}
    </Box>
  );
}

export interface HitGroup {
  key: string;
  /** The group's heading (a session's label across sessions); null for a session's own search. */
  label: string | null;
  hits: AssistantSearchHit[];
}

interface SearchHitsProps {
  search: TranscriptSearch;
  groups: HitGroup[];
  /** Who wrote the line ("You", "Assistant", "Scope request"…). */
  whoOf: (hit: AssistantSearchHit) => string;
  /** A hit's link (across sessions); else it's a button that calls `onOpen`. */
  hrefOf?: ((hit: AssistantSearchHit) => string) | undefined;
  onOpen?: ((hit: AssistantSearchHit) => void) | undefined;
  /** Shown under the hits when there are more than the search returned and it can't page on. */
  moreNote?: string | undefined;
}

export function SearchHits({ search, groups, whoOf, hrefOf, onOpen, moreNote }: SearchHitsProps) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const locale = useLocale();
  const zone = useMe().user.timezone ?? 'UTC';
  const count = groups.reduce((sum, group) => sum + group.hits.length, 0);
  // More's page landed: focus moves to its first hit, since More may be gone (nothing older).
  const root = useRef<HTMLDivElement | null>(null);
  const focusFrom = useRef<number | null>(null);
  useEffect(() => {
    const from = focusFrom.current;
    if (from === null || count <= from) return;
    focusFrom.current = null;
    root.current?.querySelectorAll<HTMLElement>('[data-search-hit] > *')[from]?.focus();
  }, [count]);
  const status =
    search.error !== null
      ? ''
      : search.hits === null
        ? t('assistant.search.searching')
        : count === 0
          ? t('assistant.search.noMatches')
          : t('assistant.search.count', { count });

  function hit(line: AssistantSearchHit) {
    const body = (
      <>
        <Typography component="span" sx={{ display: 'block', fontSize: 12, color: tokens.text2 }}>
          {t('assistant.search.hitMeta', {
            who: whoOf(line),
            when: formatInstant(line.createdAt, locale, zone),
          })}
        </Typography>
        <Typography
          component="span"
          sx={{ display: 'block', fontSize: 13, color: tokens.text, overflowWrap: 'anywhere' }}
          data-snippet=""
        >
          {line.snippet}
        </Typography>
      </>
    );
    const sx = { display: 'block', py: 0.75, px: 1.5, borderRadius: 1 } as const;
    return (
      <Box component="li" key={`${line.sessionId ?? ''}-${line.id}`} data-search-hit={line.id}>
        {hrefOf !== undefined ? (
          <ListItemButton
            component={RouterLink}
            to={hrefOf(line)}
            onClick={() => onOpen?.(line)}
            sx={sx}
          >
            {body}
          </ListItemButton>
        ) : (
          <ListItemButton
            component="button"
            onClick={() => onOpen?.(line)}
            sx={{ ...sx, width: '100%', textAlign: 'start' }}
          >
            {body}
          </ListItemButton>
        )}
      </Box>
    );
  }

  return (
    <Box ref={root} data-search-results="">
      <Typography
        role="status"
        aria-live="polite"
        sx={{ fontSize: 12, color: tokens.text2, px: 1.5, py: 0.5 }}
        data-search-status=""
      >
        {status}
      </Typography>
      {search.hits !== null &&
        groups
          .filter((group) => group.hits.length > 0)
          .map((group) => (
            <Box key={group.key} data-search-group={group.key}>
              {group.label !== null && (
                <Typography
                  component="h3"
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: tokens.text,
                    px: 1.5,
                    pt: 1,
                    overflowWrap: 'anywhere',
                  }}
                >
                  {group.label}
                </Typography>
              )}
              <List
                component="ul"
                disablePadding
                aria-label={group.label ?? t('assistant.search.results')}
                sx={{ listStyle: 'none', m: 0 }}
              >
                {group.hits.map(hit)}
              </List>
            </Box>
          ))}
      {search.canMore && (
        <Box sx={{ px: 1.5, py: 0.5 }}>
          <Button
            size="small"
            aria-busy={search.loadingMore}
            onClick={() => {
              if (search.loadingMore) return;
              focusFrom.current = count;
              search.more();
            }}
          >
            {t('assistant.search.more')}
          </Button>
        </Box>
      )}
      {search.hasMore && !search.canMore && moreNote !== undefined && (
        <Typography sx={{ fontSize: 12, color: tokens.text2, px: 1.5, py: 0.5 }}>
          {moreNote}
        </Typography>
      )}
    </Box>
  );
}
